import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  DriverElectroCompatibilityFormsPayload,
  GetDriverElectroCompatibilityFormsResponse,
} from "./electrificationApi";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";

export interface ElectrificationState {
  driverElectroCompatibilityFormsStatus: QueryStatus;
  driverElectroCompatibilityForms: GetDriverElectroCompatibilityFormsResponse;
}

const initialState: ElectrificationState = {
  driverElectroCompatibilityFormsStatus: "idle",
  driverElectroCompatibilityForms: [],
};

export const driverElectroCompatibilityFormsAsync = createAsyncThunk(
  "driverElectroCompatibilityFormsAsync/call",
  async (payload: DriverElectroCompatibilityFormsPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/electrification/drivers`, payload);
  },
);

export const getDriverElectroCompatibilityFormsAsync = createAsyncThunk(
  "getDriverElectroCompatibilityFormsAsync/call",
  async (_, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response =
      await axios.get<GetDriverElectroCompatibilityFormsResponse>(
        `/electrification/drivers/company/${company?.uuid}`,
      );
    return response.data;
  },
);

export const electrificationSlice = createSlice({
  name: "electrification",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetDriverElectroCompatibilityFormsStatus(state) {
      state.driverElectroCompatibilityFormsStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(driverElectroCompatibilityFormsAsync.pending, (state) => {
        state.driverElectroCompatibilityFormsStatus = "processing";
      })
      .addCase(driverElectroCompatibilityFormsAsync.fulfilled, (state) => {
        state.driverElectroCompatibilityFormsStatus = "success";
      })
      .addCase(driverElectroCompatibilityFormsAsync.rejected, (state) => {
        state.driverElectroCompatibilityFormsStatus = "failed";
      })
      .addCase(getDriverElectroCompatibilityFormsAsync.pending, (state) => {
        state.driverElectroCompatibilityFormsStatus = "processing";
      })
      .addCase(
        getDriverElectroCompatibilityFormsAsync.fulfilled,
        (state, action) => {
          state.driverElectroCompatibilityForms = action.payload;
        },
      )
      .addCase(getDriverElectroCompatibilityFormsAsync.rejected, (state) => {
        state.driverElectroCompatibilityFormsStatus = "failed";
      });
  },
});

export const selectDriverElectroCompatibilityFormsStatus = (state: RootState) =>
  state.electrification.driverElectroCompatibilityFormsStatus;

export const selectDriverElectroCompatibilityForms = (state: RootState) =>
  state.electrification.driverElectroCompatibilityForms;

export default electrificationSlice.reducer;
